import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FiSliders, FiX } from 'react-icons/fi';

import api from '../../services/api';
import { getMaterials, getProjectCategories } from '../../services/lookups';
import {
  useFilters,
  PRICE_FILTERS,
  SORT_OPTIONS,
} from '../../hooks/projectFilters';
import { useFavorites } from '../../hooks/favorites';
import LoadingIndicator from '../LoadingIndicator';
import Notification from '../Notification';
import NoProjectsFound from '../NoProjectsFound';
import Paginator from '../Paginator';
import ProjectCard from '../ProjectCard';
import ProjectsFilters from '../ProjectsFilters';
import SelectDropdown from '../SelectDropdown';

import {
  ActiveChip,
  ActiveChips,
  ClearAll,
  Content,
  DrawerBody,
  DrawerHeader,
  FilterDrawer,
  FilterDrawerButton,
  Layout,
  Overlay,
  ProjectsList,
  ResultCount,
  Sidebar,
  Toolbar,
} from './styles';

const propTypes = {
  limit: PropTypes.number,
  onlyFavorites: PropTypes.bool,
  emptyText: PropTypes.string,
};

const defaultProps = {
  limit: 12,
  onlyFavorites: false,
  emptyText: 'Nenhum projeto encontrado',
};

/**
 * Listagem de projetos com filtros, ordenação e paginação.
 *
 * É a mesma vitrine usada na home, na página de projetos e nos favoritos —
 * gratuitos e pagos dividem a listagem e a paginação, e os filtros valem para
 * os dois.
 */
const ProjectsExplorer = ({ limit, onlyFavorites, emptyText }) => {
  const [projects, setProjects] = useState([]);
  const [pagesCount, setPagesCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [categoryNames, setCategoryNames] = useState({});
  const [materialNames, setMaterialNames] = useState({});

  const { isFavorite } = useFavorites();

  const {
    currentPage,
    setCurrentPage,
    selectedMaterials,
    setSelectedMaterials,
    selectedCategories,
    setSelectedCategories,
    premium,
    setPremium,
    price,
    setPrice,
    sort,
    setSort,
    searchString,
    setSearchString,
  } = useFilters();

  useEffect(() => {
    (async () => {
      try {
        const [categories, materials] = await Promise.all([
          getProjectCategories(),
          getMaterials(),
        ]);
        setCategoryNames(Object.fromEntries(categories.map(c => [c.id, c.name])));
        setMaterialNames(Object.fromEntries(materials.map(m => [m.id, m.name])));
      } catch {
        // Sem os nomes os chips somem, mas a listagem continua funcionando.
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(
          onlyFavorites ? '/favorite_projects' : '/projects',
          {
            params: {
              categories: selectedCategories,
              materials: selectedMaterials,
              premium,
              price,
              sort,
              page: currentPage,
              limit,
              searchString,
            },
          }
        );

        if (cancelled) return;

        setProjects(data.projects);
        setTotalCount(data.count);
        setPagesCount(Math.ceil(data.count / limit));
      } catch {
        if (!cancelled) {
          Notification({
            message: 'Erro ao buscar projetos',
            type: Notification.types.ERROR,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedMaterials,
    selectedCategories,
    premium,
    price,
    sort,
    currentPage,
    searchString,
    limit,
    onlyFavorites,
  ]);

  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  function handlePageChange(selectedPage) {
    const scroller = document.getElementById('scroller');
    if (scroller) scroller.scroll(0, 0);
    setCurrentPage(selectedPage);
  }

  function handleSortChange(value) {
    setCurrentPage(1);
    setSort(value);
  }

  function clearAll() {
    setCurrentPage(1);
    setSelectedCategories([]);
    setSelectedMaterials([]);
    setPremium(false);
    setPrice('all');
    setSearchString('');
  }

  // Na tela de favoritos o desfavoritar tem de tirar o card da lista na hora —
  // sem isso o projeto continuaria visível (e contado) até recarregar.
  const visibleProjects = onlyFavorites
    ? projects.filter(p => isFavorite(p.id))
    : projects;

  const removedFromPage = projects.length - visibleProjects.length;
  const visibleCount = Math.max(totalCount - removedFromPage, 0);

  const activeChips = [
    ...(searchString
      ? [
          {
            key: 'search',
            label: searchString,
            onRemove: () => setSearchString(''),
          },
        ]
      : []),
    ...selectedCategories.map(id => ({
      key: `category-${id}`,
      label: categoryNames[id] || 'Categoria',
      onRemove: () =>
        setSelectedCategories(selectedCategories.filter(i => i !== id)),
    })),
    ...selectedMaterials.map(id => ({
      key: `material-${id}`,
      label: materialNames[id] || 'Material',
      onRemove: () =>
        setSelectedMaterials(selectedMaterials.filter(i => i !== id)),
    })),
    ...(premium
      ? [{ key: 'premium', label: 'Pro', onRemove: () => setPremium(false) }]
      : []),
    ...(price !== 'all'
      ? [
          {
            key: 'price',
            label:
              PRICE_FILTERS.find(option => option.value === price)?.label ||
              'Preço',
            onRemove: () => setPrice('all'),
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <Sidebar>
        <ProjectsFilters idPrefix="sidebar" />
      </Sidebar>

      <Content>
        <Toolbar>
          <FilterDrawerButton
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir filtros"
          >
            <FiSliders size={16} />
            Filtros
          </FilterDrawerButton>

          <ResultCount>
            {isLoading
              ? 'Buscando projetos...'
              : `${visibleCount} ${
                  visibleCount === 1 ? 'resultado' : 'resultados'
                }`}
          </ResultCount>

          <SelectDropdown
            options={SORT_OPTIONS}
            value={sort}
            onChange={handleSortChange}
            ariaLabel="Ordenar por"
          />
        </Toolbar>

        {activeChips.length > 0 && (
          <ActiveChips>
            {activeChips.map(chip => (
              <ActiveChip key={chip.key}>
                {chip.label}
                <button
                  type="button"
                  aria-label={`Remover filtro ${chip.label}`}
                  onClick={() => {
                    setCurrentPage(1);
                    chip.onRemove();
                  }}
                >
                  <FiX size={12} />
                </button>
              </ActiveChip>
            ))}
            <ClearAll type="button" onClick={clearAll}>
              Limpar tudo
            </ClearAll>
          </ActiveChips>
        )}

        {/* O giro fica na área de resultados, e não no `.loader` global: a
            listagem também é uma seção no meio da home, e um spinner de posição
            fixa aparecia centralizado na tela, por cima do topo da página. */}
        {isLoading && <LoadingIndicator text={null} />}

        {!isLoading &&
          (visibleProjects.length === 0 ? (
            <NoProjectsFound text={emptyText} />
          ) : (
            <ProjectsList>
              {visibleProjects.map(p => (
                <ProjectCard project={p} key={p.id} />
              ))}
            </ProjectsList>
          ))}

        {pagesCount > 1 && (
          <Paginator
            onPageChange={handlePageChange}
            pagesCount={pagesCount}
            initialPage={currentPage}
          />
        )}
      </Content>

      <Overlay isOpen={isDrawerOpen} onClick={() => setDrawerOpen(false)} />
      <FilterDrawer isOpen={isDrawerOpen}>
        <DrawerHeader>
          <strong>Filtros</strong>
          <button
            type="button"
            aria-label="Fechar filtros"
            onClick={() => setDrawerOpen(false)}
          >
            <FiX size={20} />
          </button>
        </DrawerHeader>
        <DrawerBody>
          <ProjectsFilters idPrefix="drawer" />
        </DrawerBody>
      </FilterDrawer>
    </Layout>
  );
};

ProjectsExplorer.propTypes = propTypes;
ProjectsExplorer.defaultProps = defaultProps;

export default ProjectsExplorer;
